import { DestroyRef, inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';

/**
 * Implemented by any component that can lose unsaved user input when the user navigates away.
 * Pair it with `unsavedChangesGuard` (blocks in-app Router navigation) and
 * `warnOnBrowserUnloadIfUnsaved` (blocks browser tab close/refresh/typed URL) to cover both
 * exit paths from a single boolean check.
 */
export interface CanComponentDeactivate {
  /** Returns true when the component holds edits the user has not saved yet. */
  hasUnsavedChanges(): boolean;
}

/**
 * `CanDeactivate` route guard that blocks in-app navigation (router links, `Router.navigate()`,
 * browser back/forward while the SPA is running) away from a component with unsaved changes.
 *
 * Shows the shared `ConfirmDialogComponent` instead of the native `confirm()` popup so the
 * prompt matches the rest of the app's UX.
 *
 * Usage:
 * ```ts
 * export class MyEditComponent implements CanComponentDeactivate {
 *   hasUnsavedChanges(): boolean {
 *     return this.form.dirty;
 *   }
 * }
 * ```
 * ```ts
 * { path: 'my/edit', component: MyEditComponent, canDeactivate: [unsavedChangesGuard] }
 * ```
 */
export const unsavedChangesGuard: CanDeactivateFn<CanComponentDeactivate> = (
  component: CanComponentDeactivate
): boolean | Observable<boolean> => {
  if (!component.hasUnsavedChanges()) {
    return true;
  }

  const dialog: MatDialog = inject(MatDialog);
  const dialogRef = dialog.open(ConfirmDialogComponent, {
    width: '450px',
    data: {
      dialogMessage: 'You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.',
      confirmText: 'Leave',
      cancelText: 'Stay'
    }
  });

  return dialogRef.afterClosed().pipe(map((confirmed: boolean) => !!confirmed));
};

/**
 * Registers a `window: beforeunload` listener that triggers the browser's native "leave site?"
 * prompt when `hasUnsavedChanges()` returns true. This is the only way to intercept a hard
 * browser action (tab close, refresh, typed URL, external link) -- `CanDeactivate` guards only
 * run for in-app Angular Router navigation.
 *
 * Why this exists: Angular's router guards cannot observe browser-level navigation. Wiring the
 * native event by hand in every component would duplicate the same three lines everywhere.
 * How it works: the listener is added once and removed automatically via `DestroyRef` --
 * call it from a component constructor (or any injection context) with no manual cleanup needed.
 * When NOT to use it: for in-app navigation, use `unsavedChangesGuard` instead -- browsers ignore
 * custom messages here and only show their own generic confirmation text.
 *
 * @param hasUnsavedChanges Callback returning true when there are unsaved edits to warn about.
 */
export function warnOnBrowserUnloadIfUnsaved(hasUnsavedChanges: () => boolean): void {
  const destroyRef: DestroyRef = inject(DestroyRef);

  const beforeUnloadHandler = (event: BeforeUnloadEvent): void => {
    if (!hasUnsavedChanges()) {
      return;
    }
    // `returnValue` is deprecated in the DOM spec (preventDefault() alone is the modern
    // signal) but Chrome and Edge still require it to be set to actually show the native
    // "leave site?" prompt. Access it through a narrow local type instead of `any` so the
    // deprecated `BeforeUnloadEvent.returnValue` typing (and its lint warning) is bypassed
    // without losing type safety on the rest of the event.
    event.preventDefault();
    (event as unknown as { returnValue: string }).returnValue = '';
  };

  window.addEventListener('beforeunload', beforeUnloadHandler);
  destroyRef.onDestroy(() => window.removeEventListener('beforeunload', beforeUnloadHandler));
}
